import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useLanguage } from '../lib/language';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Sidebar from '../components/Sidebar';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  Users,
  Images,
  DollarSign,
  Loader2,
  RefreshCcw,
  TrendingUp,
  CreditCard,
  Camera,
  LayoutGrid,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [stats, setStats] = useState(null);
  const [photographers, setPhotographers] = useState([]);
  const [settlements, setSettlements] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedPhotographer, setSelectedPhotographer] = useState(null);
  const [photographerDetail, setPhotographerDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  useEffect(() => {
    // Wait for user data to load
    if (!user) return;
    
    if (user.role !== 'admin') {
      toast.error(language === 'pl' ? 'Brak dostępu administratora' : 'Admin access required');
      navigate('/dashboard');
      return;
    }
    fetchData();
  }, [user, language, navigate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, photosRes, settlesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/photographers`),
        axios.get(`${API}/admin/settlements`)
      ]);
      setStats(statsRes.data);
      setPhotographers(photosRes.data.photographers);
      setSettlements(settlesRes.data.settlements);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error(language === 'pl' ? 'Błąd pobierania danych' : 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/admin/seed`);
      toast.success(language === 'pl' ? 'Dane przykładowe dodane!' : 'Sample data seeded!');
      console.log('Seeded data:', response.data);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Seed failed');
    } finally {
      setSeeding(false);
    }
  };

  const fetchPhotographerDetail = async (id) => {
    setLoadingDetail(true);
    try {
      const res = await axios.get(`${API}/admin/photographers/${id}`);
      setPhotographerDetail(res.data);
      setSelectedPhotographer(id);
    } catch (error) {
      toast.error(language === 'pl' ? 'Błąd pobierania danych fotografa' : 'Failed to fetch photographer data');
    } finally {
      setLoadingDetail(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar activePath="/admin" />

      {/* Main Content */}
      <main className="lg:ml-64 p-6 lg:p-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold">
              {language === 'pl' ? 'Panel Administratora' : 'Admin Panel'}
            </h1>
            <p className="text-muted-foreground">
              {language === 'pl' ? 'Zarządzaj platformą Focus Photo' : 'Manage Focus Photo platform'}
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={fetchData}>
              <RefreshCcw className="w-4 h-4 mr-2" />
              {language === 'pl' ? 'Odśwież' : 'Refresh'}
            </Button>
            <Button onClick={handleSeedData} disabled={seeding}>
              {seeding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <TrendingUp className="w-4 h-4 mr-2" />}
              {language === 'pl' ? 'Dodaj dane testowe' : 'Seed Data'}
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8">
          <Button
            variant={activeTab === 'overview' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('overview')}
          >
            <LayoutGrid className="w-4 h-4 mr-2" />
            {language === 'pl' ? 'Przegląd' : 'Overview'}
          </Button>
          <Button
            variant={activeTab === 'photographers' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('photographers')}
          >
            <Users className="w-4 h-4 mr-2" />
            {language === 'pl' ? 'Fotografowie' : 'Photographers'}
          </Button>
          <Button
            variant={activeTab === 'settlements' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTab('settlements')}
          >
            <CreditCard className="w-4 h-4 mr-2" />
            {language === 'pl' ? 'Rozliczenia' : 'Settlements'}
          </Button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && stats && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {language === 'pl' ? 'Użytkownicy' : 'Users'}
                      </p>
                      <p className="text-3xl font-bold mt-1">{stats.total_users}</p>
                    </div>
                    <Users className="w-10 h-10 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {language === 'pl' ? 'Fotografowie' : 'Photographers'}
                      </p>
                      <p className="text-3xl font-bold mt-1">{stats.total_photographers}</p>
                    </div>
                    <Camera className="w-10 h-10 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {language === 'pl' ? 'Galerie' : 'Galleries'}
                      </p>
                      <p className="text-3xl font-bold mt-1">{stats.total_galleries}</p>
                    </div>
                    <Images className="w-10 h-10 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground">
                        {language === 'pl' ? 'Przychody' : 'Earnings'}
                      </p>
                      <p className="text-3xl font-bold mt-1">{stats.total_earnings} PLN</p>
                    </div>
                    <DollarSign className="w-10 h-10 text-muted-foreground" />
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>{language === 'pl' ? 'Statystyki zdjęć' : 'Photo Stats'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{language === 'pl' ? 'Łącznie zdjęć' : 'Total Photos'}</span>
                      <span className="font-semibold">{stats.total_photos}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{language === 'pl' ? 'Prośby o retusz' : 'Retouch Requests'}</span>
                      <span className="font-semibold">{stats.total_retouch_requests}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-muted-foreground">{language === 'pl' ? 'Zrealizowane retusze' : 'Completed Retouches'}</span>
                      <span className="font-semibold text-green-500">{stats.completed_retouch_requests}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border">
                <CardHeader>
                  <CardTitle>{language === 'pl' ? 'Szybkie akcje' : 'Quick Actions'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button variant="outline" className="w-full justify-start" onClick={handleSeedData} disabled={seeding}>
                    <TrendingUp className="w-4 h-4 mr-2" />
                    {language === 'pl' ? 'Dodaj przykładowych fotografów i galerie' : 'Add sample photographers and galleries'}
                  </Button>
                  <Button variant="outline" className="w-full justify-start" onClick={fetchData}>
                    <RefreshCcw className="w-4 h-4 mr-2" />
                    {language === 'pl' ? 'Odśwież dane' : 'Refresh data'}
                  </Button>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        )}

        {/* Photographers Tab */}
        {activeTab === 'photographers' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>{language === 'pl' ? 'Lista fotografów' : 'Photographers List'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Imię' : 'Name'}</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Plan' : 'Plan'}</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Galerie' : 'Galleries'}</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Zdjęcia' : 'Photos'}</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Przychody' : 'Earnings'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {photographers.map((p) => (
                        <tr key={p.id} className="border-b border-border/50 hover:bg-secondary/30 cursor-pointer" onClick={() => fetchPhotographerDetail(p.id)}>
                          <td className="py-3 px-4 font-medium text-blue-400 hover:text-blue-300">{p.name}</td>
                          <td className="py-3 px-4 text-muted-foreground">{p.email}</td>
                          <td className="py-3 px-4">
                            <span className={`px-2 py-1 rounded-sm text-xs ${
                              p.plan === 'pro' ? 'bg-blue-500/20 text-blue-400' :
                              p.plan === 'studio' ? 'bg-purple-500/20 text-purple-400' :
                              p.plan === 'enterprise' ? 'bg-yellow-500/20 text-yellow-400' :
                              'bg-gray-500/20 text-gray-400'
                            }`}>
                              {p.plan?.toUpperCase() || 'FREE'}
                            </span>
                          </td>
                          <td className="py-3 px-4">{p.stats?.galleries || 0}</td>
                          <td className="py-3 px-4">{p.stats?.photos || 0}</td>
                          <td className="py-3 px-4 font-medium text-green-500">{p.stats?.earnings || 0} PLN</td>
                        </tr>
                      ))}
                      {photographers.length === 0 && (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-muted-foreground">
                            {language === 'pl' ? 'Brak fotografów' : 'No photographers found'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
            {/* Photographer Detail Panel */}
            {loadingDetail && (
              <div className="mt-6 flex justify-center"><Loader2 className="w-6 h-6 animate-spin" /></div>
            )}
            {selectedPhotographer && photographerDetail && !loadingDetail && (
              <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="mt-6">
                <Card className="bg-card border-border">
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle>
                      {photographerDetail.photographer.name} — {language === 'pl' ? 'Szczegóły' : 'Details'}
                    </CardTitle>
                    <Button variant="ghost" size="sm" onClick={() => { setSelectedPhotographer(null); setPhotographerDetail(null); }}>✕</Button>
                  </CardHeader>
                  <CardContent>
                    {/* Stats row */}
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                      {[
                        { label: language === 'pl' ? 'Galerie' : 'Galleries', value: photographerDetail.stats.galleries },
                        { label: language === 'pl' ? 'Zdjęcia' : 'Photos', value: photographerDetail.stats.total_photos },
                        { label: language === 'pl' ? 'Klienci' : 'Clients', value: photographerDetail.stats.clients },
                        { label: language === 'pl' ? 'Zamówienia' : 'Orders', value: photographerDetail.stats.orders },
                        { label: language === 'pl' ? 'Rezerwacje' : 'Bookings', value: photographerDetail.stats.bookings },
                      ].map((s, i) => (
                        <div key={i} className="bg-secondary/30 rounded-lg p-3 text-center">
                          <p className="text-2xl font-bold">{s.value}</p>
                          <p className="text-xs text-muted-foreground">{s.label}</p>
                        </div>
                      ))}
                    </div>

                    {/* Galleries */}
                    <h3 className="font-semibold mb-3">{language === 'pl' ? 'Galerie' : 'Galleries'}</h3>
                    {photographerDetail.galleries.length === 0 ? (
                      <p className="text-muted-foreground text-sm mb-4">{language === 'pl' ? 'Brak galerii' : 'No galleries'}</p>
                    ) : (
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">{language === 'pl' ? 'Nazwa' : 'Name'}</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">{language === 'pl' ? 'Zdjęcia' : 'Photos'}</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Layout</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">{language === 'pl' ? 'Utworzona' : 'Created'}</th>
                            </tr>
                          </thead>
                          <tbody>
                            {photographerDetail.galleries.map((g) => (
                              <tr key={g.id} className="border-b border-border/30">
                                <td className="py-2 px-3 font-medium">{g.name}</td>
                                <td className="py-2 px-3">{g.photo_count || 0}</td>
                                <td className="py-2 px-3 text-muted-foreground">{g.layout}</td>
                                <td className="py-2 px-3 text-muted-foreground">{g.created_at ? new Date(g.created_at).toLocaleDateString('pl') : '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Clients */}
                    <h3 className="font-semibold mb-3">{language === 'pl' ? 'Klienci' : 'Clients'}</h3>
                    {photographerDetail.clients.length === 0 ? (
                      <p className="text-muted-foreground text-sm mb-4">{language === 'pl' ? 'Brak klientów' : 'No clients'}</p>
                    ) : (
                      <div className="overflow-x-auto mb-6">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">{language === 'pl' ? 'Imię' : 'Name'}</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Email</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">{language === 'pl' ? 'Telefon' : 'Phone'}</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Tagi</th>
                            </tr>
                          </thead>
                          <tbody>
                            {photographerDetail.clients.map((c) => (
                              <tr key={c.id} className="border-b border-border/30">
                                <td className="py-2 px-3 font-medium">{c.name}</td>
                                <td className="py-2 px-3 text-muted-foreground">{c.email}</td>
                                <td className="py-2 px-3 text-muted-foreground">{c.phone || '-'}</td>
                                <td className="py-2 px-3">{(c.tags || []).map(t => <span key={t} className="inline-block bg-secondary/50 rounded px-1.5 py-0.5 text-xs mr-1">{t}</span>)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {/* Orders */}
                    <h3 className="font-semibold mb-3">{language === 'pl' ? 'Zamówienia' : 'Orders'}</h3>
                    {photographerDetail.orders.length === 0 ? (
                      <p className="text-muted-foreground text-sm">{language === 'pl' ? 'Brak zamówień' : 'No orders'}</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-border">
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Nr</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">{language === 'pl' ? 'Klient' : 'Client'}</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">{language === 'pl' ? 'Kwota' : 'Amount'}</th>
                              <th className="text-left py-2 px-3 font-medium text-muted-foreground">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {photographerDetail.orders.map((o) => (
                              <tr key={o.id} className="border-b border-border/30">
                                <td className="py-2 px-3 font-mono text-xs">{o.order_number}</td>
                                <td className="py-2 px-3">{o.client_name}</td>
                                <td className="py-2 px-3 font-medium">{o.total_amount} PLN</td>
                                <td className="py-2 px-3">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    o.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                                    o.status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                                    'bg-gray-500/20 text-gray-400'
                                  }`}>{o.status}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </motion.div>
        )}

        {/* Settlements Tab */}
        {activeTab === 'settlements' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="bg-card border-border">
              <CardHeader>
                <CardTitle>{language === 'pl' ? 'Rozliczenia z fotografami' : 'Photographer Settlements'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Fotograf' : 'Photographer'}</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">Email</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Zamówienia' : 'Orders'}</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Przychód brutto' : 'Gross'}</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Prowizja (10%)' : 'Fee (10%)'}</th>
                        <th className="text-left py-3 px-4 font-medium text-muted-foreground">{language === 'pl' ? 'Do wypłaty' : 'Payout'}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {settlements.map((s) => (
                        <tr key={s.photographer_id} className="border-b border-border/50 hover:bg-secondary/30">
                          <td className="py-3 px-4 font-medium">{s.photographer_name}</td>
                          <td className="py-3 px-4 text-muted-foreground">{s.photographer_email}</td>
                          <td className="py-3 px-4">{s.total_orders}</td>
                          <td className="py-3 px-4">{s.total_earnings.toFixed(2)} PLN</td>
                          <td className="py-3 px-4 text-red-400">-{s.platform_fee.toFixed(2)} PLN</td>
                          <td className="py-3 px-4 font-medium text-green-500">{s.payout_amount.toFixed(2)} PLN</td>
                        </tr>
                      ))}
                      {settlements.length === 0 && (
                        <tr>
                          <td colSpan={6} className="py-8 text-center text-muted-foreground">
                            {language === 'pl' ? 'Brak rozliczeń' : 'No settlements found'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}
      </main>
    </div>
  );
}
